import * as yup from 'yup'
import { londonToday } from 'lib/dayjs'

import { isValidEmail, phoneNumberValidation } from 'lib/formik/schemata'
import { composeIsoDate } from 'components/form/helpers'

// mirrors server/src/shared/api/quotes/probate/interfaces.ts ASSET_OWNERSHIP
export const ASSET_OWNERSHIP = {
  SOLE: 'sole',
  JOINT: 'joint',
  NOT_ASKED: 'not_asked',
} as const

// mirrors server/src/shared/api/quotes/probate/interfaces.ts UTILITY_TYPE
export const UTILITY_TYPE = {
  ELECTRICITY: 'electricity',
  GAS: 'gas',
  WATER: 'water',
  BROADBAND: 'broadband',
  LANDLINE_PHONE: 'landline_phone',
  MOBILE_PHONE: 'mobile_phone',
  TV_LICENCE: 'tv_licence',
  SATELLITE_OR_CABLE_TV: 'satellite_or_cable_tv',
  OTHER: 'other',
} as const

// mirrors server/src/shared/api/quotes/probate/interfaces.ts PROPERTY_TYPE
const PROPERTY_TYPE = {
  RENTAL: 'rental',
  OVERSEAS: 'overseas',
  SECOND_HOME: 'second_home',
  OTHER: 'other',
} as const

// mirrors server/src/shared/config/enums.ts PROPERTY_OWNERSHIP
const PROPERTY_OWNERSHIP = {
  SOLE: 'solely_owned',
  JOINT_TENANTS: 'joint_tenants',
  TENANTS_IN_COMMON: 'tenants_in_common',
  UNSURE: 'unsure',
} as const

// mirrors server/src/shared/config/enums.ts RELATIONSHIP_TO_PERSON
export const RELATIONSHIP_TO_PERSON = {
  HUSBAND: 'husband',
  WIFE: 'wife',
  CIVIL_PARTNER: 'civil_partner',
  PARTNER_UNMARRIED: 'partner_unmarried',
  FATHER: 'father',
  MOTHER: 'mother',
  CHILD: 'child',
  DAUGHTER: 'daughter',
  SON: 'son',
  BIOLOGICAL_CHILD: 'biological_child',
  ADOPTED_CHILD: 'adopted_child',
  STEP_CHILD: 'step_child',
  GRANDCHILD: 'grandchild',
  GRANDMOTHER: 'grandmother',
  GRANDFATHER: 'grandfather',
  AUNT: 'aunt',
  UNCLE: 'uncle',
  SIBLING: 'sibling',
  SISTER: 'sister',
  BROTHER: 'brother',
  NIECE: 'niece',
  NEPHEW: 'nephew',
  COUSIN: 'cousin',
  OTHER_FAMILY: 'other_family',
  FRIEND: 'friend',
  CLIENT: 'client',
  OTHER: 'other',
} as const

// mirrors server/src/probate/api/probate-estates/schemata.ts
const TITLE_OPTIONS = ['MR', 'MRS', 'MISS', 'MS', null]
const YES_NO_UNSURE_OPTIONS = ['yes', 'no', 'unsure', null]
const SOLE_OR_JOINT_OWNERSHIP = [
  ASSET_OWNERSHIP.SOLE,
  ASSET_OWNERSHIP.JOINT,
  null,
]

const MUST_BE_A_NUMBER = 'Must be a number'
const MUST_BE_A_WHOLE_NUMBER = 'Must be a whole number'
const CHOOSE_AN_OPTION = 'Choose one of the options'

const text = (max = 255) =>
  yup
    .string()
    .nullable()
    .trim()
    .max(
      max,
      ({ max: limit }: { max: number }) =>
        `Must be ${limit.toLocaleString('en-GB')} characters or fewer`
    )

const longText = () => text(50000)

const email = () =>
  text().test('valid-email', 'Email is invalid.', (value) =>
    value ? isValidEmail(value) : true
  )

// currency inputs hold their comma-formatted display value (e.g.
// "50,000,000"), which yup can't cast to a number on its own
// Only a bank account can be negative, when it's overdrawn.
const currency = ({ canBeNegative = false } = {}) =>
  yup
    .number()
    .transform((value, originalValue) => {
      if (typeof originalValue !== 'string') return value
      if (originalValue.trim() === '') return null
      return Number(originalValue.replace(/,/g, ''))
    })
    .typeError(MUST_BE_A_NUMBER)
    .nullable()
    .min(
      canBeNegative ? -10000000000 : 0,
      canBeNegative
        ? 'That overdraft is too big to record'
        : 'Must be 0 or more'
    )
    .max(10000000000, 'Must be 10,000,000,000 or less')

// Day/Month/Year inputs sit alongside the composed date, so the rules
// read the parts - a date that doesn't exist composes to nothing
type DateParent = Record<string, unknown>

const dateFromParts = (parent: DateParent, name: string) => {
  const parts = {
    day: String(parent[`${name}Day`] ?? ''),
    month: String(parent[`${name}Month`] ?? ''),
    year: String(parent[`${name}Year`] ?? ''),
  }
  const isComplete = !!(parts.day && parts.month && parts.year)
  return { isComplete, isoDate: composeIsoDate(parts) }
}

const isInFuture = (isoDate: string | null) =>
  !!isoDate && isoDate > londonToday()

const dateFromPartsSchema = (name: string, label: string) =>
  yup
    .mixed()
    .nullable()
    .test('real-date', 'Enter a real date', function () {
      const { isComplete, isoDate } = dateFromParts(this.parent, name)
      return !isComplete || !!isoDate
    })
    .test('not-in-future', `${label} can’t be in the future`, function () {
      return !isInFuture(dateFromParts(this.parent, name).isoDate)
    })

// mirrors server/src/shared/api/addresses/schemata.js addressFields
const ADDRESS_FIELDS = {
  lineOne: text(),
  lineTwo: text(),
  city: text(),
  postalCode: text(),
  countryCode: text(2),
}

// mirrors server/src/probate/api/probate-estates/people/types.ts personalRepresentativeRole
const PERSONAL_REPRESENTATIVE_ROLE = {
  ACTING: 'acting',
  POWER_RESERVED: 'power_reserved',
  RENOUNCING: 'renouncing',
  UNABLE_TO_ACT: 'unable_to_act',
  DEAD: 'dead',
} as const

// firstName/lastName mirror probate_cases.deceased_first_name/deceased_last_name
export const DECEASED_SCHEMA = yup.object().shape({
  title: yup.string().oneOf(TITLE_OPTIONS, CHOOSE_AN_OPTION).nullable(),
  firstName: text(),
  lastName: text(),
  nationalInsuranceNumber: yup
    .string()
    .nullable()
    .test(
      'nationalInsuranceNumber',
      'National insurance number must be 9 letters and numbers only, no symbols or spaces',
      (value) => {
        if (!value || value.trim() === '') return true
        return /^[A-Z0-9]{9}$/i.test(value.trim())
      }
    ),
  address: yup.object().nullable().shape(ADDRESS_FIELDS),
  dateOfBirth: dateFromPartsSchema('dateOfBirth', 'Date of birth'),
  dateOfDeath: dateFromPartsSchema('dateOfDeath', 'Date of death').test(
    'after-birth',
    'Date of death must be after the date of birth',
    function () {
      const birth = dateFromParts(this.parent, 'dateOfBirth').isoDate
      const death = dateFromParts(this.parent, 'dateOfDeath').isoDate
      return !birth || !death || death >= birth
    }
  ),
  placeOfDeath: text(),
  occupation: text(),
  deceasedNotes: longText(),
})

export const WILL_OR_INTESTACY_SCHEMA = yup.object().shape({
  hasWill: yup.boolean().nullable(),
  willLocation: longText(),
  willOrIntestacyNotes: longText(),
})

// mirrors server/src/probate/api/probate-estates/people/schemata.ts
export const SHARED_PERSON_SCHEMA = yup.object().shape({
  title: text(),
  firstNames: text(),
  lastName: text(),
  relationshipToDeceased: yup
    .string()
    .nullable()
    .oneOf([...Object.values(RELATIONSHIP_TO_PERSON), null], CHOOSE_AN_OPTION),
  dateOfBirth: dateFromPartsSchema('dateOfBirth', 'Date of birth'),
})

const PERSON_CONTACT_FIELDS = {
  address: yup.object().nullable().shape(ADDRESS_FIELDS),
  email: email(),
  phoneNumber: text().test(...phoneNumberValidation),
  notes: longText(),
}

export const EXECUTOR_SCHEMA = SHARED_PERSON_SCHEMA.shape({
  ...PERSON_CONTACT_FIELDS,
  personalRepresentativeRole: yup
    .string()
    .nullable()
    .oneOf(
      [...Object.values(PERSONAL_REPRESENTATIVE_ROLE), null],
      CHOOSE_AN_OPTION
    ),
})

// mirrors server/src/probate/api/probate-estates/people/types.ts canBeContactedReason
const CAN_BE_CONTACTED_REASON = {
  DECEASED: 'deceased',
  ESTRANGED: 'estranged',
  UNABLE_TO_ACT: 'unable_to_act',
} as const

export const BENEFICIARY_SCHEMA = SHARED_PERSON_SCHEMA.shape({
  ...PERSON_CONTACT_FIELDS,
  canBeContacted: yup.boolean().nullable(),
  canBeContactedReason: yup
    .string()
    .nullable()
    .oneOf([...Object.values(CAN_BE_CONTACTED_REASON), null], CHOOSE_AN_OPTION),
})

const DECEASED_SHARE_OF_JOINT_OWNERSHIP = yup
  .number()
  .typeError(MUST_BE_A_NUMBER)
  .min(0, 'Must be between 0 and 100')
  .max(100, 'Must be between 0 and 100')
  .nullable()

export const SHARED_PROPERTY_SCHEMA = yup.object().shape({
  isMainResidence: yup.boolean().nullable(),
  address: yup.object().nullable().shape(ADDRESS_FIELDS),
  ownership: yup
    .string()
    .oneOf(SOLE_OR_JOINT_OWNERSHIP, CHOOSE_AN_OPTION)
    .nullable(),
  deceasedShareOfJointOwnership: DECEASED_SHARE_OF_JOINT_OWNERSHIP,
  jointOwnershipType: yup
    .string()
    .nullable()
    .oneOf(
      [
        PROPERTY_OWNERSHIP.JOINT_TENANTS,
        PROPERTY_OWNERSHIP.TENANTS_IN_COMMON,
        PROPERTY_OWNERSHIP.UNSURE,
        null,
      ],
      CHOOSE_AN_OPTION
    ),
  propertyType: yup
    .string()
    .nullable()
    .oneOf([...Object.values(PROPERTY_TYPE), null], CHOOSE_AN_OPTION),
  coOwnerIds: yup.array().of(yup.number()),
  tenure: yup
    .string()
    .nullable()
    .oneOf(['leasehold', 'freehold', 'unsure', null], CHOOSE_AN_OPTION),
  isCurrentlyOccupied: yup.boolean().nullable(),
  isInsured: yup
    .string()
    .nullable()
    .oneOf(YES_NO_UNSURE_OPTIONS, CHOOSE_AN_OPTION),
  insuranceProviderName: text(),
  insurancePolicyNumber: text(),
  // mirrors the server, which only accepts whole pounds for properties
  value: currency().integer(MUST_BE_A_WHOLE_NUMBER),
  isEstimatedValue: yup.boolean().nullable(),
  notes: longText(),
})

// mirrors server/src/shared/api/quotes/probate/interfaces.ts VEHICLE_TYPE
const VEHICLE_TYPE = {
  CAR: 'car',
  MOTORBIKE: 'motorbike',
  VAN: 'van',
  CARAVAN_OR_MOTORHOME: 'caravan_or_motorhome',
  BOAT: 'boat',
  OTHER: 'other',
} as const

// mirrors server/src/shared/api/quotes/probate/interfaces.ts VEHICLE_CONDITION
const VEHICLE_CONDITION = {
  EXCELLENT: 'excellent',
  GOOD: 'good',
  FAIR: 'fair',
  POOR: 'poor',
} as const

export const VEHICLE_SCHEMA = yup.object().shape({
  // the type is what marks the item as a vehicle, so it can't be cleared
  vehicleType: yup
    .string()
    .nullable()
    .required('Choose a vehicle type')
    .oneOf(Object.values(VEHICLE_TYPE), CHOOSE_AN_OPTION),
  description: text(),
  referenceNumber: text(),
  vehicleCondition: yup
    .string()
    .nullable()
    .oneOf([...Object.values(VEHICLE_CONDITION), null], CHOOSE_AN_OPTION),
  value: currency(),
  notes: longText(),
})

// Confirmed against the Figma "Belongings and vehicles" screen: the
// cash/insurance/safe-deposit-box summary fields sit on the same screen as
// the vehicle/possession item list, not on the deceased-details screen
// where they previously lived - so this section is an object combining
// both, unlike the other item sections which are plain arrays.
export const PERSONAL_POSSESSIONS_AND_VEHICLES_SCHEMA = yup.object().shape({
  householdPossessionsValue: currency(),
  cashFoundAtHome: currency(),
  householdItemsInsured: yup
    .string()
    .nullable()
    .oneOf(YES_NO_UNSURE_OPTIONS, CHOOSE_AN_OPTION),
  hasSafeDepositBox: yup
    .string()
    .nullable()
    .oneOf(YES_NO_UNSURE_OPTIONS, CHOOSE_AN_OPTION),
  personalPossessionsNotes: longText(),
  safeDepositBoxNotes: longText(),
  vehicles: yup.array().of(VEHICLE_SCHEMA),
})

const ITEM_VALUE = currency()
const ITEM_NOTES = longText()

export const BANK_ACCOUNT_SCHEMA = yup.object().shape({
  providerName: text(),
  // mirrors server/src/probate/api/probate-estates/items/schemata.ts
  accountNumber: text().test(
    'account-number',
    'Account number must be 8 digits',
    (value) => !value || /^\d{8}$/.test(value.replace(/\s/g, ''))
  ),
  sortCode: text().test(
    'sort-code',
    'Sort code must be 6 digits, like 12-34-56',
    (value) => !value || /^\d{2}[- ]?\d{2}[- ]?\d{2}$/.test(value)
  ),
  ownership: yup
    .string()
    .nullable()
    .oneOf(SOLE_OR_JOINT_OWNERSHIP, CHOOSE_AN_OPTION),
  deceasedShareOfJointOwnership: DECEASED_SHARE_OF_JOINT_OWNERSHIP,
  // mirrors server/src/probate/api/probate-estates/items/schemata.ts
  value: currency({ canBeNegative: true }),
  notes: ITEM_NOTES,
})

export const PREMIUM_BOND_SCHEMA = yup.object().shape({
  referenceNumber: text(),
  value: ITEM_VALUE,
  notes: ITEM_NOTES,
})

export const NSANDI_SCHEMA = yup.object().shape({
  referenceNumber: text(),
  value: ITEM_VALUE,
  notes: ITEM_NOTES,
})

export const PUBLIC_SHAREHOLDING_SCHEMA = yup.object().shape({
  providerName: text(),
  referenceNumber: text(),
  stockbrokerName: text(),
  ownership: yup
    .string()
    .nullable()
    .oneOf(SOLE_OR_JOINT_OWNERSHIP, CHOOSE_AN_OPTION),
  deceasedShareOfJointOwnership: DECEASED_SHARE_OF_JOINT_OWNERSHIP,
  value: ITEM_VALUE,
  notes: ITEM_NOTES,
})

// mirrors server/src/probate/api/probate-estates/advisors/schemata.ts
export const ADVISOR_SCHEMA = yup.object().shape({
  fullName: text(),
  accountReference: text(),
  email: email(),
  phoneNumber: text().test(...phoneNumberValidation),
  notes: ITEM_NOTES,
})

export const LIFE_INSURANCE_POLICY_SCHEMA = yup.object().shape({
  providerName: text(),
  referenceNumber: text(),
  ownership: yup
    .string()
    .nullable()
    .oneOf([...Object.values(ASSET_OWNERSHIP), null], CHOOSE_AN_OPTION),
  deceasedShareOfJointOwnership: DECEASED_SHARE_OF_JOINT_OWNERSHIP,
  value: ITEM_VALUE,
  notes: ITEM_NOTES,
})

export const PRIVATE_PENSION_SCHEMA = yup.object().shape({
  providerName: text(),
  referenceNumber: text(),
  value: ITEM_VALUE,
  notes: ITEM_NOTES,
})

export const STATE_BENEFIT_SCHEMA = yup.object().shape({
  providerName: text(),
  referenceNumber: text(),
  notes: ITEM_NOTES,
})

export const UTILITY_PROVIDER_SCHEMA = yup.object().shape({
  // the type is what marks the item as a utility provider (untyped ones are
  // other bills), so it can't be cleared - as with VEHICLE_SCHEMA's vehicleType
  utilityType: yup
    .string()
    .nullable()
    .required('Choose a utility type')
    .oneOf(Object.values(UTILITY_TYPE), CHOOSE_AN_OPTION),
  providerName: text(),
  referenceNumber: text(),
  value: currency(),
  notes: longText(),
})

export const FUNERAL_BILL_SCHEMA = yup.object().shape({
  funeralBillPaid: yup.boolean().nullable(),
  funeralBillAmount: currency(),
  // mirrors the server's 5000 limit, not the usual 50000 for notes
  funeralAmountNotes: text(5000),
})

export const OTHER_BILL_SCHEMA = yup.object().shape({
  // who the bill is from - description, as the quote and old fact-find save
  // it; mirrors the server's 1000 limit so older, longer ones stay valid
  description: text(1000),
  referenceNumber: text(),
  value: currency(),
  notes: longText(),
})

// a cleared Day/Month/Year input is '', which yup can't cast to a number
const datePart = (min: number, max: number, message: string) =>
  yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .typeError(MUST_BE_A_NUMBER)
    .integer(MUST_BE_A_WHOLE_NUMBER)
    .min(min, message)
    .max(max, message)
    .nullable()

const DATE_PART_RANGES = { day: [1, 31], month: [1, 12], year: [1000, 9999] }

const approximateDateParts = (parent: DateParent, name: string) => ({
  day: String(parent[`${name}Day`] ?? ''),
  month: String(parent[`${name}Month`] ?? ''),
  year: String(parent[`${name}Year`] ?? ''),
})

type DateParts = ReturnType<typeof approximateDateParts>

// each part out of range already has its own error, so the whole date
// isn't checked until they're all fine
const arePartsInRange = (parts: DateParts) =>
  (Object.keys(DATE_PART_RANGES) as (keyof DateParts)[]).every((part) => {
    if (!parts[part]) return true
    const [min, max] = DATE_PART_RANGES[part]
    const number = Number(parts[part])
    return Number.isInteger(number) && number >= min && number <= max
  })

// a missing month or day counts as the 1st, so 2020 is checked as 2020-01-01
const approximateIsoDate = ({ day, month, year }: DateParts) =>
  year ? composeIsoDate({ day: day || '1', month: month || '1', year }) : null

// an approximate date: just a year, a month and year, or a full date
const approximateDateSchema = (name: string, label: string) =>
  yup
    .mixed()
    .nullable()
    .test(
      'whole-parts',
      'Enter a year, a month and year, or a full date',
      function () {
        const { day, month, year } = approximateDateParts(this.parent, name)
        return !((day && !month) || ((day || month) && !year))
      }
    )
    .test('real-date', 'Enter a real date', function () {
      const parts = approximateDateParts(this.parent, name)
      if (!parts.year || !arePartsInRange(parts)) return true
      return !!approximateIsoDate(parts)
    })
    .test('not-in-future', `${label} can’t be in the future`, function () {
      const parts = approximateDateParts(this.parent, name)
      if (!arePartsInRange(parts)) return true
      return !isInFuture(approximateIsoDate(parts))
    })

// the date's parts save together, once they make a whole approximate date
export const GIFT_SCHEMA = yup.object().shape({
  description: text(1000),
  gift: approximateDateSchema('gift', 'Date of gift'),
  giftDay: datePart(1, 31, 'Day must be between 1-31.'),
  giftMonth: datePart(1, 12, 'Month must be between 1-12.'),
  giftYear: datePart(1000, 9999, 'Year must be a 4 digit number, e.g. 2020.'),
  value: currency(),
  notes: longText(),
})

export const TRUSTS_SCHEMA = yup.object().shape({
  hadRightToBenefitFromTrustAssets: yup
    .string()
    .nullable()
    .oneOf(YES_NO_UNSURE_OPTIONS, CHOOSE_AN_OPTION),
  wasTrustee: yup.boolean().nullable(),
  trustAssetsNotes: longText(),
})

// mirrors server/src/probate/api/probate-estates/dependents/schemata.ts
export const DEPENDENT_SCHEMA = SHARED_PERSON_SCHEMA.shape({
  notes: longText(),
})

export const POWER_OF_ATTORNEY_SCHEMA = yup.object().shape({
  hadPowerOfAttorneyOrDeputyship: yup.boolean().nullable(),
  powerOfAttorneyNotes: longText(),
})

export const ANY_OTHER_NOTES_SCHEMA = yup.object().shape({
  notesNotCoveredElsewhere: longText(),
})
